/**
 * What each version of Terminal brought, in the platform's own words.
 *
 * **Curated here rather than read from the catalogue.** The catalogue carries
 * one sentence per release — written when the release was cut, for the update
 * notice inside the application — and that is the wrong shape for a page
 * somebody reads to decide whether Terminal is for them. Here a version gets a
 * headline of a few words and a paragraph that says what actually changed.
 *
 * **No dates.** Operator, 2026-09-07: versions only. A date on a release note
 * invites the reader to work out how long the gaps were, which is a fact about
 * us rather than about the product; the version is what a holder compares
 * against the number in their own Settings.
 *
 * Not every version is here and that is deliberate — the early ones were
 * plumbing. The recent ones are, in full.
 */

export interface ReleaseNote {
  version: string
  /** A few words. This is the whole row in the table. */
  headline: string
  /** What changed, for the page that lists them in full. Two or three sentences. */
  detail: string
}

/** Newest first. The table shows the first few; the page shows all of them. */
export const RELEASE_NOTES: ReleaseNote[] = [
  {
    version: '0.4.15',
    headline: 'You, in your own corner',
    detail:
      'Your profile has its own screen now, reached from the avatar in the bottom-left ' +
      'corner \u2014 it says which wallet Terminal is signed in as, lets you sign out, and ' +
      'carries the same settings as the website. Settings moved to the opposite corner. ' +
      'Voting also stopped showing a validation error where the proposals should be.',
  },
  {
    version: '0.4.14',
    headline: 'A market you can read at a glance',
    detail:
      'Stocks now shows what each company traded at, how far that sits from the oracle, how ' +
      'much depth the pool has and the shape of its last day \u2014 and the dashboard, which ' +
      'was empty without bots, opens on the six that moved most. Also fixes an emoji picker ' +
      'whose groups had slid into columns, and a card under Stocks and Community that said ' +
      'the feature was not in this version while the feature was on the screen above it.',
  },
  {
    version: '0.4.13',
    headline: 'Your profile, your clan, your vote',
    detail:
      'Community joins the sidebar: change the name and tag other holders see, look at the ' +
      'clans, and vote on what is open \u2014 signed by the wallet you signed in with, never ' +
      'by Terminal. What you sign is handed down by the platform rather than assembled here, ' +
      'so a vote cast from the desktop is the same vote counted anywhere else.',
  },
  {
    version: '0.4.12',
    headline: 'Mentions that reach somebody',
    detail:
      'Type @ and Channels suggests who you mean \u2014 by the name they chose, not by forty ' +
      'characters of address \u2014 including @all for everyone on the channel. Being ' +
      'mentioned now raises a notification of its own, and reaches you even on a channel you ' +
      'muted: muting says do not tell me about every message, not do not tell me when ' +
      'somebody calls me by name. The right-hand column shows who is around, the emoji ' +
      'picker is a real one, and the Refresh button is gone \u2014 the conversation keeps ' +
      'itself up to date.',
  },
  {
    version: '0.4.11',
    headline: 'Tokenized equities, on their own screen',
    detail:
      'Stocks joins the sidebar: the thirteen tokenized equities with what the oracle last ' +
      'published for each, and \u2014 next to every one of them \u2014 when it published it. ' +
      'An equity oracle only speaks while its exchange is open, so outside trading hours the ' +
      'newest honest price is the last close, and the screen says so rather than letting a ' +
      'stale number look live.',
  },
  {
    version: '0.4.10',
    headline: 'Names, faces, and a menu in the right order',
    detail:
      'Channels now shows the name and picture people chose on MPRO Lab, instead of their ' +
      'address \u2014 an address is still what you see for anyone who has not set a profile. ' +
      'The unread count appears beside Channels in the sidebar, not only on the application ' +
      'icon, and the menu is ordered the way a session actually runs: Dashboard, Channels, ' +
      'Terminal, and the rest.',
  },
  {
    version: '0.4.9',
    headline: 'Updates download only what changed',
    detail:
      'Until now every update pulled the whole application, because the file that says which ' +
      'parts changed was never published. It is now, so an update takes a fraction of the ' +
      'bandwidth and time it used to. Opening Models before choosing a folder is also no longer ' +
      'treated as a fault \u2014 it is a step you have not taken yet, and it says so.',
  },
  {
    version: '0.4.8',
    headline: 'Models says what to do, not what broke',
    detail:
      'Opening Models before choosing where Terminal keeps its files answered with a ' +
      'programming error instead of an instruction. It now says to pick a folder first, ' +
      'which is the one thing there was to do.',
  },
  {
    version: '0.4.7',
    headline: 'Channels: the controls you were missing',
    detail:
      'Folders, channels and invitations are now offered to everyone who can reach a channel, ' +
      'rather than to clan leaders alone. Every speaker has a colour and a circle, and a run of ' +
      'messages from one person says the name once. The banner at the top of Channels is gone.',
  },
  {
    version: '0.4.6',
    headline: 'Channels becomes a communicator',
    detail:
      'Channels takes the whole window, and the community channel leads the list. Reply to a ' +
      'message, react to it, mention somebody by address, or take your own message back. Attach ' +
      'images, video and PDFs — shown in the conversation rather than as a list of filenames. ' +
      'Shared links get a card with the site\u2019s own title and picture, read once by the ' +
      'platform so that no reader\u2019s machine ever contacts the site. A column beside the ' +
      'conversation shows who is on the channel and who is around. New messages arrive as a ' +
      'system notification and a count on the icon, and any channel can be muted.',
  },
  {
    version: '0.4.5',
    headline: 'Channels, the marketplace, and a wallet you can read',
    detail:
      'The communicator arrives: a channel for the community, one for your clan, folders and channels you arrange yourself, and people you invite one at a time. The marketplace opens on both sides — strategies and bots for sale, and requests from people who want one built. The wallet panel shows what you hold on Ethereum and Base with send and receive, and every settings item became a screen of its own.'
  },
  {
    version: '0.4.4',
    headline: 'One password, and one wallet panel',
    detail:
      'The password that opens Terminal is now the one that seals your wallet — there is no second passphrase to invent and forget. Changing it re-seals every account in the same step, and it is asked for again before a recovery phrase or a private key is ever shown. The wallet panel shows your balances on Ethereum and Base, with send, receive and a QR of your address.'
  },
  {
    version: '0.4.3',
    headline: 'A licence bound elsewhere can be released',
    detail:
      'When the platform says your licence is already running on another Terminal, the licence step now says so and offers to release it and finish here — instead of stopping with an error and no way forward.'
  },
  {
    version: '0.4.2',
    headline: 'Your licence follows its own files',
    detail:
      'Terminal moves its files into the folder you choose, and after that move it finds them again. A Terminal that already had a licence no longer asks for one on the next start.'
  },
  {
    version: '0.4.1',
    headline: 'A licence added once, and updates that stay quiet',
    detail:
      'Terminal remembers your licence until you remove it in Settings. An update found while the application is open downloads silently and asks only for a restart — the way an update should behave when you are in the middle of something.'
  },
  {
    version: '0.4.0',
    headline: 'The terminal and the assistant',
    detail:
      'Both arrive, each running a language model on your own machine. The terminal writes and runs code and asks before it acts; the assistant reads and explains, and cannot act at all. Nothing you type reaches us.'
  },
  {
    version: '0.3.5',
    headline: 'Updates install themselves',
    detail:
      'Terminal asked the catalogue what was newest and never told its own updater, so no restart ever fetched anything. It does now.'
  },
  {
    version: '0.3.4',
    headline: 'Signed, notarised, and proven to open',
    detail:
      'Terminal opens on a double-click on macOS: signed, notarised, stapled, and verified by starting the packaged application before the release goes out.'
  }
]
